import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CalendarIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import {
  dateToString,
  localeNormalizer,
  classNames,
} from '@youshift/shared/utils';
import { Country, Holiday } from '@youshift/shared/types';
import { useSetHolidaysMutation } from '@youshift/shared/hooks/mutations/chains';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import Wrapper from '../../../../components/Wrapper';
import YearlyCalendar from '../../../../components/Calendar/Yearly/YearlyCalendar';
import { useChainContext } from '../../../../layouts/ChainLayout';
import { useManagerContext } from '../../../../layouts/ManagerLayout';
import i18n from '../../../../utils/i18n';
import { NagerHoliday } from './types';
import { getCCAAISO, getStateISO } from './helpers';

export default function HolidayCalendar() {
  const { t } = useTranslation();
  const { idChain } = useParams();
  const { allChains, user } = useManagerContext();
  const { org } = user;
  const { country, state } = org || {};
  const chain = allChains.chains[Number(idChain)];
  const [selectedHolidays, setSelectedHolidays] = useState<Holiday[]>([]);
  const [isLoadingHolidays, setIsLoadingHolidays] = useState(false);
  const locale = localeNormalizer(i18n.language);
  const queryClient = useQueryClient();

  const setHolidaysMutation = useSetHolidaysMutation(queryClient, {
    onSuccess: () => {
      toast.success(t('manager.holidays.successSaving'));
    },
  });

  // Get today's date one year from now
  const todayInAYear = new Date();
  todayInAYear.setFullYear(todayInAYear.getFullYear() + 1);

  const fetchHolidays = async () => {
    if (!country) {
      toast.error(t('manager.holidays.noCountrySelected'));
      return;
    }

    setIsLoadingHolidays(true);
    try {
      const startDate = chain.start_day
        ? new Date(chain.start_day)
        : new Date();
      const endDate = chain.end_day
        ? new Date(chain.end_day)
        : new Date(todayInAYear);
      const startYear = startDate.getFullYear();
      const endYear = endDate.getFullYear();

      const stateCode = state
        ? country === Country.US
          ? getStateISO(state)
          : country === Country.ES
            ? getCCAAISO(state)
            : undefined
        : undefined;

      // Fetch holidays for each year in the range
      const years = Array.from(
        { length: endYear - startYear + 1 },
        (_, i) => startYear + i,
      );

      const allHolidays = await Promise.all(
        years.map(async year => {
          const url = `https://date.nager.at/api/v3/PublicHolidays/${year}/${country}`;
          const response = await fetch(url);
          if (!response.ok) throw new Error('Failed to fetch holidays');
          return response.json();
        }),
      ).then(results => results.flat());

      // Filter holidays by date range and state (if applicable)
      const filteredHolidays = allHolidays
        .filter(h => {
          const holidayDate = new Date(h.date);
          return holidayDate >= startDate && holidayDate <= endDate;
        })
        .filter(h => {
          if (stateCode) {
            return !h.counties || h.counties.includes(stateCode);
          }
          return !h.counties;
        });

      const formattedHolidays: Holiday[] = filteredHolidays.map(h => ({
        date: h.date,
        name: h.localName,
      }));

      setSelectedHolidays(prev => {
        // Merge with existing holidays, avoiding duplicates
        const existingDates = new Set(prev.map(h => h.date));
        const newHolidays = formattedHolidays.filter(
          h => !existingDates.has(h.date),
        );
        return [...prev, ...newHolidays];
      });

      toast.success(t('manager.holidays.holidaysFetched'));
    } catch (error) {
      toast.error(t('manager.holidays.errorFetching'));
      console.error('Error fetching holidays:', error);
    } finally {
      setIsLoadingHolidays(false);
    }
  };

  const handleDateClick = (date: string) => {
    setSelectedHolidays(prev => {
      const existingHoliday = prev.find(h => h.date === date);
      if (existingHoliday) {
        return prev.filter(h => h.date !== date);
      }
      return [...prev, { date, name: '' }];
    });
  };

  const handleNameChange = (date: string, name: string) => {
    setSelectedHolidays(prev =>
      prev.map(holiday =>
        holiday.date === date ? { ...holiday, name } : holiday,
      ),
    );
  };

  const handleSave = () => {
    if (!idChain) return;
    setHolidaysMutation.mutate({
      id_chain: Number(idChain),
      holidays: selectedHolidays,
    });
  };

  return (
    <Wrapper>
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1">
          <YearlyCalendar
            // TODO: remove el case para cuando start_day is not defined
            startDate={chain.start_day ? new Date(chain.start_day) : new Date()}
            endDate={
              chain.end_day ? new Date(chain.end_day) : new Date(todayInAYear)
            }
            selectedDates={selectedHolidays.map(h => h.date)}
            onDateClick={handleDateClick}
          />
        </div>
        <div className="w-full h-full lg:w-80 bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 whitespace-normal">
              {t('manager.holidays.selectedHolidays')}
            </h2>
            <button
              type="button"
              onClick={handleSave}
              disabled={
                setHolidaysMutation.isPending || selectedHolidays.length === 0
              }
              className={classNames(
                'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                (setHolidaysMutation.isPending ||
                  selectedHolidays.length === 0) &&
                  'opacity-50 cursor-not-allowed',
              )}
            >
              {setHolidaysMutation.isPending
                ? t('generic.saving')
                : t('generic.save')}
            </button>
          </div>
          {selectedHolidays.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 mb-4">
                {t('manager.holidays.noHolidaysSelected')}
              </p>
              <button
                type="button"
                onClick={fetchHolidays}
                disabled={isLoadingHolidays || !country}
                className={classNames(
                  'px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500',
                  (isLoadingHolidays || !country) &&
                    'opacity-50 cursor-not-allowed',
                )}
              >
                <ArrowPathIcon
                  className={classNames(
                    'h-4 w-4 inline mr-1',
                    isLoadingHolidays && 'animate-spin',
                  )}
                />
                {t('manager.holidays.fetchHolidays')}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {selectedHolidays
                .sort(
                  (a, b) =>
                    new Date(a.date).getTime() - new Date(b.date).getTime(),
                )
                .map(holiday => (
                  <div
                    key={holiday.date}
                    className="flex flex-col gap-2 p-2 bg-gray-50 rounded"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-900">
                        {dateToString(holiday.date, 'long', locale)}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDateClick(holiday.date)}
                        className="text-red-600 hover:text-red-800"
                      >
                        ×
                      </button>
                    </div>
                    <input
                      type="text"
                      value={holiday.name || ''}
                      onChange={e =>
                        handleNameChange(holiday.date, e.target.value)
                      }
                      placeholder={t('manager.holidays.holidayNamePlaceholder')}
                      className="text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  );
}
